import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { ActualSelected, Utils } from '../../../../../shared/utils';
import { currentActivityOption, EntityFormOption, InjectedOptions, optionId } from '../../../entity-form/entity-form.utils';
import {
  CreditDialogData,
  creditTitle,
  displayDate,
  EMPTY_GUID,
  fromApiDate,
  roundMoney,
  sortByDateDesc,
  toApiDate,
} from '../../models/credit-shared.model';
import { CreditOperationsService } from '../../services/credit-operations.service';
import { CreditLookupsService } from '../../services/credit-lookups.service';

@Component({
  selector: 'app-credit-short-term-debt-dialog',
  templateUrl: './credit-short-term-debt-dialog.component.html',
  styleUrls: ['../../styles/credit-dialog.scss'],
  standalone: false,
})
export class CreditShortTermDebtDialogComponent implements OnInit {
  ready = false;
  loadError = false;
  saving = false;
  savingMessage = '';
  submitted = false;
  changed = false;
  private requestedEditDone = false;

  items: any[] = [];
  editing: any | null = null;

  activities: EntityFormOption[] = [];

  form: UntypedFormGroup;

  private readonly injected = new InjectedOptions();

  static open(dialog: MatDialog, data: CreditDialogData): Observable<boolean> {
    return dialog.open<CreditShortTermDebtDialogComponent, CreditDialogData, boolean>(CreditShortTermDebtDialogComponent, {
      width: '620px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<CreditShortTermDebtDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: CreditDialogData,
    private operations: CreditOperationsService,
    private lookups: CreditLookupsService,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.form = this.fb.group({
      fecha: [new Date()],
      concepto: [''],
      importe: [null],
      activity: [null],
    });
  }

  get isEmployee(): boolean {
    return this.data.kind === 'employee';
  }

  get subtitle(): string {
    return creditTitle(this.data.kind, this.data.credit);
  }

  get yearHint(): string {
    return this.isEmployee ? '' : 'Ejercicio ' + ActualSelected.yearSelected;
  }

  get total(): number {
    return roundMoney(this.items.reduce((acc, item) => acc + (Number(item.importe) || 0), 0));
  }

  get missingDate(): boolean { return !this.form.get('fecha').value; }
  get missingConcept(): boolean { return !String(this.form.get('concepto').value || '').trim(); }
  get invalidAmount(): boolean { return !Number(this.form.get('importe').value); }
  get missingActivity(): boolean { return optionId(this.form.get('activity').value) === null; }

  control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  ngOnInit(): void {
    this.load();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  startEdit(item: any): void {
    this.editing = item;
    this.submitted = false;
    this.injected.clear();
    this.form.patchValue({
      fecha: fromApiDate(item.fecha),
      concepto: item.concepto || '',
      importe: item.importe != null ? Number(item.importe) : null,
      activity: this.injected.ensure(this.activities, item.activitiesUid, item.activitiesName),
    }, { emitEvent: false });
  }

  cancelEdit(): void {
    this.editing = null;
    this.resetForm();
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    if (this.missingDate || this.missingConcept || this.invalidAmount || this.missingActivity) {
      return;
    }

    const value = this.form.getRawValue();
    const isEdit = !!this.editing;
    const payload = this.editing ? { ...this.editing } : this.operations.newShortTermDebt(this.data.kind);
    payload[this.operations.parentKey(this.data.kind)] = this.data.credit.uid;
    payload.fecha = toApiDate(value.fecha);
    payload.concepto = value.concepto;
    payload.importe = Number(value.importe) || 0;
    payload.activitiesUid = optionId(value.activity);

    this.saving = true;
    this.savingMessage = isEdit ? 'Actualizando deuda a corto plazo…' : 'Registrando deuda a corto plazo…';
    this.dialogRef.disableClose = true;

    this.operations.saveShortTermDebt(this.data.kind, payload, isEdit)
      .then(ok => { if (ok !== true) throw new Error(); })
      .then(() => {
        this.changed = true;
        this.notify.ok(isEdit ? 'Deuda a corto plazo actualizada correctamente' : 'Deuda a corto plazo registrada correctamente');
        if (!isEdit || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.editing = null;
        this.resetForm();
        this.loadItems(false);
      })
      .catch(error => {
        const serverMessage = error && error.error && error.error.message;
        this.notify.error(serverMessage || 'No se pudo guardar la deuda a corto plazo');
      })
      .then(() => {
        this.saving = false;
        this.dialogRef.disableClose = false;
      });
  }

  remove(item: any): void {
    if (this.saving) return;
    this.confirm.open({
      title: 'Eliminar deuda a corto plazo',
      subtitle: this.subtitle || undefined,
      message: 'Se eliminará la reclasificación a corto plazo y su asiento contable. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.itemDetails(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando deuda a corto plazo…',
      errorText: 'No se pudo eliminar la deuda a corto plazo',
      action: () => this.operations.deleteShortTermDebt(this.data.kind, item.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changed = true;
      this.notify.ok('Deuda a corto plazo eliminada correctamente');
      if (this.editing && this.editing.uid === item.uid) this.cancelEdit();
      this.loadItems(false);
    });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changed);
  }

  trackByUid(_: number, item: any): string {
    return item.uid;
  }

  itemDate(item: any): string {
    return displayDate(item.fecha);
  }

  itemMeta(item: any): string {
    return [item.concepto, item.activitiesName].filter(Boolean).join(' · ');
  }


  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const item = this.items.find(entry => entry.uid === this.data.editUid);
    if (item) this.startEdit(item);
  }

  private load(): void {
    this.ready = false;
    Promise.all([
      this.operations.listShortTermDebts(this.data.kind, this.data.credit.uid),
      this.lookups.activities(),
    ])
      .then(([list, activities]) => {
        this.activities = (activities || [])
          .filter(activity => activity && activity.uid && activity.uid !== EMPTY_GUID)
          .map(activity => ({ id: activity.uid, display: activity.name || '' }));
        this.items = sortByDateDesc(list, 'fecha');
        this.applySingleOptionDefaults();
        this.openRequestedEdit();
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  private loadItems(initial: boolean): void {
    if (initial) this.ready = false;
    this.operations.listShortTermDebts(this.data.kind, this.data.credit.uid)
      .then(list => {
        this.items = sortByDateDesc(list, 'fecha');
        this.openRequestedEdit();
      })
      .catch(() => {
        if (initial) this.loadError = true;
        else this.notify.error('No se pudieron recargar las deudas a corto plazo');
      })
      .then(() => { this.ready = true; });
  }

  private applySingleOptionDefaults(): void {
    if (this.editing) return;
    if (optionId(this.form.get('activity').value) === null) {
      const current = currentActivityOption(this.activities);
      if (current) this.control('activity').setValue(current, { emitEvent: false });
      else if (this.activities.length === 1) this.control('activity').setValue(this.activities[0], { emitEvent: false });
    }
  }

  private resetForm(): void {
    this.injected.clear();
    this.submitted = false;
    this.form.reset({ fecha: new Date(), concepto: '', importe: null, activity: null }, { emitEvent: false });
    this.applySingleOptionDefaults();
  }

  private itemDetails(item: any): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (item.fecha) details.push({ label: 'Fecha', value: displayDate(item.fecha) });
    if (item.concepto) details.push({ label: 'Concepto', value: item.concepto });
    details.push({ label: 'Importe', value: Utils.FormatMoney(Number(item.importe) || 0) + ' €' });
    return details;
  }
}
